"use client";
import { useCompletion } from "@ai-sdk/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowDown, ChevronDown } from "lucide-react";
import Lunr from "lunr";
import { useLocale, useTranslations } from "next-intl";
import { useQueryState } from "nuqs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { LoadingElement } from "@/components/LoadingElement";
import { Button } from "@/components/ui/button";
import * as RadixForm from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { debounce } from "@/lib/debounce";
import { cn } from "@/lib/utils";
import { getCaptions, getYoutubeInfo, type YoutubeInfo } from "./actions";

export const YoutubeChat = () => {
  const t = useTranslations();
  const lang = useLocale();
  const [url, setUrl] = useQueryState("url", {
    defaultValue: "",
    clearOnDefault: true,
  });
  const [captions, setCaptions] = useState<string[]>([]);
  const [youtubeInfo, setYoutubeInfo] = useState<YoutubeInfo>();
  const [filteredCaptions, setFilteredCaptions] = useState<string[]>([]);
  const [lunrIndex, setLunrIndex] = useState<any>();
  const [captionsState, setCaptionsState] = useState<string>("INITIAL");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const nGrammer = (string: string) => generateNGrams(string, 2, 3);

  const scrollableChatRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    scrollableChatRef.current?.scrollTo({
      top: scrollableChatRef.current.scrollHeight,
      behavior: "smooth",
    });
  };
  const [isScrolledDown, setIsScrolledDown] = useState(true);
  useEffect(() => {
    if (scrollableChatRef.current) {
      scrollableChatRef.current.addEventListener("scroll", (e) => {
        const el = e.target as HTMLDivElement;
        const isScrolledDown = el.scrollHeight - el.scrollTop === el.clientHeight;
        setIsScrolledDown(isScrolledDown);
      });
    }
  }, [scrollableChatRef]);

  const { completion, stop, complete, isLoading, input, error, setInput } = useCompletion({
    api: "/api/v1/chat-with-youtube",
    streamProtocol: "text",
  });

  const isPristineMessages = !completion && !isLoading && !error;

  const handleGroqQuery = ({ query }: { query: string }) => {
    if (!query) {
      return;
    }
    let totalChars = query.length;
    const processedQuery = nGrammer(query).join(" ");
    const hits: LunrHit[] = lunrIndex.search(processedQuery);

    // Just add segments if no hits
    hits.push(...captions.map((_segment, i) => ({ ref: i.toString(), score: 0 })));

    const filteredSegments: { text: string; index: number }[] = [];
    const pushedIndexes = new Set<number>();

    for (const hit of hits) {
      const segment = captions[Number.parseInt(hit.ref, 10)];
      totalChars += segment.length;
      if (totalChars > 4096) {
        break;
      }
      if (pushedIndexes.has(Number.parseInt(hit.ref, 10))) {
        continue;
      }
      filteredSegments.push({
        text: segment,
        index: Number.parseInt(hit.ref, 10),
      });
      pushedIndexes.add(Number.parseInt(hit.ref, 10));
    }

    filteredSegments.sort((a, b) => a.index - b.index);

    console.log({ query, hits, filteredSegments });

    const _filteredCaptions = filteredSegments.map((x) => x.text);
    setFilteredCaptions(_filteredCaptions);

    if (isLoading) {
      stop();
    } else {
      setInput(query);
      complete(query, {
        body: {
          language: lang,
          captions: _filteredCaptions,
          model: "gpt-4o-mini",
          channelTitle: youtubeInfo?.channel,
          videoTitle: youtubeInfo?.title,
        },
      });
    }
  };

  const debouncedUpdateUrl = useCallback(
    debounce((query: string) => {
      setUrl(query);
    }, 250),
    []
  );

  useEffect(() => {
    const fetchYoutubeInfo = async (url: string) => {
      setCaptionsState("LOADING");
      const youtubeInfoResponse = await getYoutubeInfo({ url });

      setYoutubeInfo(youtubeInfoResponse.yt_info);

      const videoLanguage = youtubeInfoResponse.yt_info.language;

      const captionsResponse = await getCaptions({
        url,
        language: videoLanguage,
      });

      setCaptions(captionsResponse.map((x) => x.text));
      setCaptionsState("SUCCESS");
      const lunrIndex = buildLunrIndex({
        whisperJson: { segments: captionsResponse },
      });
      setLunrIndex(Lunr.Index.load(lunrIndex));
    };

    if (isValidYoutubeUrl(url)) {
      fetchYoutubeInfo(url);
    }
  }, [url]);

  const isValidYoutubeUrl = (url: string) => {
    const youtubeIdRegex = /^[a-zA-Z0-9_-]{11}$/;

    const urlRegex = /^(https?:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+/;
    return youtubeIdRegex.test(url) || urlRegex.test(url);
  };

  return (
    <div className="mt-12">
      <div className="mb-4 flex flex-col items-center">
        <label htmlFor="chat-youtube-url" className="block font-medium mb-1 text-lg">
          {t("youtubeChat.youtubeUrlLabel")}
        </label>
        <Input
          id="chat-youtube-url"
          className="max-w-xl text-lg"
          type="url"
          placeholder="https://www.youtube.com/watch?v=yZPly6zzq0E"
          defaultValue={url}
          onChange={(e) => {
            if (isValidYoutubeUrl(e.target.value)) {
              debouncedUpdateUrl(e.target.value);
            } else if (!e.target.value) {
              setUrl("");
            }
          }}
        />
      </div>

      {url && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {(captionsState === "SUCCESS" && (
              <>
                <div
                  className="max-h-[50vh] overflow-auto space-y-8 mb-4 p-2 flex flex-col relative sub-scrollbar"
                  ref={scrollableChatRef}
                >
                  {(isPristineMessages && (
                    <div className="mx-auto my-16 rounded-lg border bg-card p-4 text-card-foreground">
                      {t("youtubeChat.noMessagesText")}
                    </div>
                  )) || (
                    <StreamResponseContent
                      error={error}
                      input={input}
                      isLoading={isLoading}
                      completion={completion}
                      scrollToBottom={scrollToBottom}
                    />
                  )}
                  {isLoading && <UserPrompt prompt={input} isCreating />}
                  <div
                    className={cn({
                      "sticky bottom-0 left-1/2 -translate-x-1/2 inline w-12 h-12": true,
                      hidden: isScrolledDown,
                    })}
                  >
                    <Button
                      className={cn({
                        "w-12 h-12 rounded-full": true,
                      })}
                      variant="outline"
                      disabled={isScrolledDown}
                      onClick={scrollToBottom}
                      size="icon"
                    >
                      <ArrowDown className="h-6 w-6" />
                    </Button>
                  </div>
                </div>
                <TranscriptionChatForm handleGroqQuery={handleGroqQuery} />
              </>
            )) || <LoadingElement />}
          </div>
          {(youtubeInfo && (
            <div>
              <div>
                <p>{youtubeInfo.title}</p>
                <img
                  src={youtubeInfo.thumbnail}
                  alt={youtubeInfo.title}
                  className="w-32 h-32 object-contain"
                />
              </div>
              <div>
                {captions && (
                  <>
                    <p>Video transcript ({captions.join("").length} characters)</p>
                    <Textarea value={captions.join("\n")} rows={12} readOnly />
                  </>
                )}
              </div>
            </div>
          )) || <LoadingElement />}
        </div>
      )}

      {(function () {
        if (captionsState !== "SUCCESS") {
          return null;
        }

        if (showAdvanced) {
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                {captions && (
                  <>
                    <p>Video transcript ({captions.join("").length} characters)</p>
                    <Textarea value={captions.join("\n")} rows={12} readOnly />
                  </>
                )}
              </div>
              <div>
                {filteredCaptions && (
                  <>
                    <p>Filtered transcript ({filteredCaptions.join("").length} characters)</p>
                    <Textarea value={filteredCaptions.join("\n")} rows={12} readOnly />
                  </>
                )}
              </div>
            </div>
          );
        } else {
          return (
            <div className="mt-4">
              <Button variant="ghost" onClick={() => setShowAdvanced(true)}>
                {t("uploadForm.advancedButton")} <ChevronDown className="ml-2" />
              </Button>
            </div>
          );
        }
      })()}
    </div>
  );
};

const TranscriptionChatForm = ({
  handleGroqQuery,
}: {
  handleGroqQuery: (data: { query: string }) => void;
}) => {
  const t = useTranslations();

  const formSchema = z.object({
    query: z.string().min(1, t("transcriptionChat.noPromptError")),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      query: "",
    },
  });

  const handleSubmit = async (formData: z.infer<typeof formSchema>) => {
    handleGroqQuery(formData);
    form.reset();
  };

  return (
    <div className="space-y-2">
      <div>
        <RadixForm.Form {...form}>
          <form className="space-y-2" onSubmit={form.handleSubmit(handleSubmit)}>
            <RadixForm.FormField
              name="query"
              control={form.control}
              render={({ field }) => (
                <RadixForm.FormItem>
                  <Input {...field} placeholder={t("transcriptionChat.promptPlaceholder")} />
                </RadixForm.FormItem>
              )}
            ></RadixForm.FormField>
            <Button type="submit">{t("transcriptionChat.submitPromptButton")}</Button>
          </form>
        </RadixForm.Form>
      </div>
    </div>
  );
};

const StreamResponseContent = ({
  error,
  input,
  isLoading,
  completion,
  scrollToBottom,
}: {
  error: Error | undefined;
  input: string;
  isLoading: boolean;
  completion: string;
  scrollToBottom: () => void;
}) => {
  const t = useTranslations();

  useEffect(() => {
    if (isLoading) {
      scrollToBottom();
    }
  }, [scrollToBottom, isLoading]);

  if (isLoading && !completion) {
    return <div>{t("transcriptionChat.loadingStreamText")}</div>;
  } else if (error) {
    //Error
    return (
      <div>
        {t("transcriptionChat.streamErrorText")}: {JSON.stringify(error, null, 2)}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <UserPrompt prompt={input} isCreating={isLoading} />
      {(error && <div>{t("transcriptionChat.streamErrorText")}</div>) || (
        <div className="whitespace-pre-wrap">{completion || "..."}</div>
      )}
    </div>
  );
};

const UserPrompt = ({ prompt, isCreating }: { prompt: string; isCreating: boolean }) => {
  return (
    <div
      className={cn({
        "flex w-fit items-center space-x-2 rounded-md bg-muted px-4 py-2 text-muted-foreground": true,
        "animate-pulse": isCreating,
      })}
    >
      <span>{prompt}</span>
      {isCreating && <Spinner className="inline size-6" />}
    </div>
  );
};

const ngramTokenizer = (minGram = 2, maxGram = 3, obj?: string | string[], metadata?: object) => {
  if (obj == null || obj === undefined) {
    return [];
  }

  if (Array.isArray(obj)) {
    return obj.map(function (t) {
      return new Lunr.Token(
        Lunr.utils.asString(t).toLowerCase(),
        metadata ? structuredClone(metadata) : {}
      );
    });
  }

  const str = obj.toString().toLowerCase();
  const tokens: Lunr.Token[] = [];

  // Generate n-grams for each length from minGram to maxGram
  str
    .replace(/\s+/, " ")
    .split(" ")
    .forEach((word) => {
      for (let n = minGram; n <= maxGram; n++) {
        for (let sliceStart = 0; sliceStart <= word.length - n; sliceStart++) {
          const sliceEnd = sliceStart + n;

          const slice = word.slice(sliceStart, sliceEnd);

          const trimmedToken = slice.replace(/^\W+/, "").replace(/\W+$/, "");
          if (trimmedToken.length < minGram) {
            continue;
          }

          const tokenMetadata = metadata ? structuredClone(metadata) : {};

          tokens.push(new Lunr.Token(trimmedToken, tokenMetadata));
        }
      }
    });

  return tokens;
};

const getNgramTokenizer = (minGram = 2, maxGram = 3): typeof Lunr.tokenizer => {
  const fn = ngramTokenizer.bind(null, minGram, maxGram) as unknown as typeof Lunr.tokenizer;
  fn.separator = Lunr.tokenizer.separator;
  return fn;
};

const buildLunrIndex = ({ whisperJson }: { whisperJson: { segments: { text: string }[] } }) => {
  const indexBuilder = new Lunr.Builder();

  indexBuilder.field("text");
  indexBuilder.tokenizer = getNgramTokenizer(2, 3);
  //indexBuilder.pipeline.add(Lunr.trimmer);
  whisperJson.segments.forEach((segment, i) => {
    indexBuilder.add({ text: segment.text, id: i });
  });

  const serializedIndex = indexBuilder.build().toJSON();

  return serializedIndex;
};

function generateNGrams(text: string, minGram: number, maxGram: number) {
  console.log("generateNGrams", text);
  if (text.length < minGram) {
    console.log("Too short word", text);
    return [];
  }
  const nGrams = [];

  // Ensure that minGram and maxGram are valid
  if (minGram <= 0 || maxGram < minGram) {
    throw new Error("Invalid ngrams");
  }

  // Generate n-grams for each length from minGram to maxGram
  for (let n = minGram; n <= maxGram; n++) {
    for (let i = 0; i <= text.length - n; i++) {
      nGrams.push(text.substring(i, i + n));
    }
  }

  return nGrams;
}
